import React, { useState, useEffect } from 'react';
import { Box, Button, Typography, CircularProgress } from '@mui/material';

interface GerenciadorBackgroundProps {
  corporationName: string;
  backendUrl: string;
}

const GerenciadorBackground: React.FC<GerenciadorBackgroundProps> = ({ corporationName, backendUrl }) => {
  const [currentBackgroundUrl, setCurrentBackgroundUrl] = useState<string | null>(null);
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchBackground = async () => {
      if (!corporationName) return;
      setIsLoading(true);
      setError(null);
      try {
        const response = await fetch(`${backendUrl}/api/arquivos/corporacao/${corporationName}/background-url`);
        if (response.ok) {
          const data = await response.json();
          setCurrentBackgroundUrl(data.url);
        } else {
          setCurrentBackgroundUrl(null); // Define como nulo se não encontrar imagem
        }
      } catch (err) {
        console.error("Erro ao buscar background", err);
        setError("Não foi possível carregar a imagem de background.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchBackground();
  }, [corporationName, backendUrl]);

  useEffect(() => {
    if (fileToUpload) {
      const url = URL.createObjectURL(fileToUpload);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setPreviewUrl(null);
    }
  }, [fileToUpload]);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFileToUpload(event.target.files[0]);
    }
  };

  const handleUpload = async () => {
    if (!fileToUpload) return;
    setIsUploading(true);
    setError(null);

    const formData = new FormData();
    formData.append('arquivo', fileToUpload);

    try {
      const response = await fetch(`${backendUrl}/api/arquivos/corporacao/${corporationName}/background`, {
        method: 'POST',
        body: formData,
      });
      if (!response.ok) {
        throw new Error("Falha no upload da imagem.");
      }
      alert('Background atualizado com sucesso!');
      // Recarrega a página para mostrar a nova imagem com uma URL pré-assinada válida
      window.location.reload();
    } catch (err: any) {
      setError(err.message || 'Ocorreu um erro no upload.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Imagem de Background</Typography>
      
      <Box sx={{ my: 2 }}>
        <Typography variant="subtitle1" gutterBottom>Background Atual</Typography>
        {isLoading && <CircularProgress size={24} />}
        {!isLoading && currentBackgroundUrl && (
          <img src={currentBackgroundUrl} alt="Background Atual" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ddd' }} />
        )}
        {!isLoading && !currentBackgroundUrl && (
          <Typography color="text.secondary">Nenhuma imagem de background definida.</Typography>
        )}
      </Box>

      <Typography variant="h6" gutterBottom>Alterar Imagem</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, maxWidth: '400px' }}>
        <Button variant="outlined" component="label">
          Selecionar Arquivo
          <input type="file" accept="image/*" hidden onChange={handleFileChange} />
        </Button>

        {previewUrl && fileToUpload && (
          <Box>
            <Typography variant="subtitle2">Preview:</Typography>
            <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: '200px', borderRadius: '8px', border: '1px solid #ddd' }} />
            <Typography variant="caption">{fileToUpload.name}</Typography>
          </Box>
        )}
        
        <Button 
          variant="contained" 
          onClick={handleUpload}
          disabled={!fileToUpload || isUploading}
        >
          {isUploading ? <CircularProgress size={24} color="inherit" /> : 'Salvar Novo Background'}
        </Button>
        {error && <Typography color="error" sx={{ mt: 1 }}>{error}</Typography>}
      </Box>
    </Box>
  );
};

export default GerenciadorBackground;